import {
  useMutation,
  useQuery,
  useQueryClient,
  useSuspenseQuery,
} from "@tanstack/react-query";
import {
  createFileRoute,
  Link,
  redirect,
  useNavigate,
} from "@tanstack/react-router";
import type { inferRouterOutputs } from "@trpc/server";
import {
  ArrowLeftIcon,
  CheckIcon,
  CircleCheck,
  CircleX,
  EditIcon,
  Merge,
  MoreVertical,
  PlusIcon,
  Trash2Icon,
  UserCircleIcon,
  UserMinusIcon,
  UserPlusIcon,
  XIcon,
} from "lucide-react";
import { Suspense, useState } from "react";
import { DialogTrigger, Heading } from "react-aria-components";
import z from "zod";

import { authClient } from "@/auth/client";
import { isAdminRole } from "@/auth/shared";
import {
  type MergeProfile,
  MergeProfilesForm,
} from "@/components/admin/merge-profiles-form";
import { Alert } from "@/components/base/alert";
import { Button, button } from "@/components/base/button";
import { CopyButton } from "@/components/base/copy-button";
import {
  DropdownMenu,
  DropdownMenuItem,
} from "@/components/base/dropdown-menu";
import { useAppForm } from "@/components/base/form";
import { Modal, ModalHeading } from "@/components/base/modal";
import { card, title } from "@/components/base/primitives";
import { Radio } from "@/components/base/radio-group";
import { Select } from "@/components/base/select";
import { tagStyles } from "@/components/base/tag-group";
import { TextField } from "@/components/base/text-field";
import { EditProfileModal } from "@/components/profiles/edit-profile-modal";
import { ProfileName } from "@/components/profiles/name";
import { ProfileRegistrations } from "@/components/profiles/profile-registrations";
import { TshirtSizeEditor } from "@/components/tournaments/tshirt-size-editor";
import {
  adminUpdateUserMutationOptions,
  adminVerifyEmailMutationOptions,
  adminVerifyPhoneMutationOptions,
} from "@/data/users";
import { roleSchema } from "@/db/schema";
import type { TshirtSize } from "@/db/schema/payments";
import { getUserQueryOptions } from "@/functions/users/get-user";
import { useTRPC } from "@/integrations/trpc/react";
import type { usersRouter } from "@/integrations/trpc/routers/users";
import { DefaultLayout } from "@/layouts/default";
import { normalizePhone, phoneValidationSchema } from "@/lib/phone";
import { TSHIRT_SIZE_OPTIONS } from "@/lib/tshirt";

export const Route = createFileRoute("/admin/user/$userId")({
  loader: async ({ context: { queryClient, trpc }, params: { userId } }) => {
    const viewer = await queryClient.ensureQueryData(
      trpc.viewer.get.queryOptions(),
    );

    if (!viewer || !isAdminRole(viewer.role)) {
      throw redirect({ to: "/not-found" });
    }

    await queryClient.ensureQueryData(getUserQueryOptions(userId));
  },
  component: RouteComponent,
});

function RouteComponent() {
  const { userId } = Route.useParams();
  const { data: user } = useSuspenseQuery(getUserQueryOptions(userId));
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const [isEditingDetails, setEditingDetails] = useState(false);
  const [isDeleteUserOpen, setIsDeleteUserOpen] = useState(false);
  const [editingProfileId, setEditingProfileId] = useState<number | null>(null);
  const [mergeKeepId, setMergeKeepId] = useState<number | null>(null);
  const [mergeDeleteId, setMergeDeleteId] = useState<number | null>(null);
  const isMergeMode = mergeKeepId !== null || mergeDeleteId !== null;
  const [isMergeUserOpen, setIsMergeUserOpen] = useState(false);
  const [createMembershipProfileId, setCreateMembershipProfileId] = useState<
    number | null
  >(null);
  const [unlinkProfileId, setUnlinkProfileId] = useState<number | null>(null);
  const [isLinkDependentOpen, setIsLinkDependentOpen] = useState(false);

  const trpc = useTRPC();
  const {
    mutate: unlinkDependent,
    isPending: isUnlinking,
    error: unlinkError,
    reset: resetUnlink,
  } = useMutation({
    ...trpc.users.adminUnlinkDependent.mutationOptions(),
    onSuccess: () => {
      setUnlinkProfileId(null);
      queryClient.invalidateQueries(getUserQueryOptions(userId));
    },
  });

  const { mutate: updateUser, failureReason: updateUserError } = useMutation({
    ...adminUpdateUserMutationOptions(),
    onSuccess: () => {
      queryClient.invalidateQueries(getUserQueryOptions(userId));
      setEditingDetails(false);
    },
  });

  const detailsSchema = z.object({
    username: z.string().optional(),
    email: z.email(),
    phoneNumber: z.literal("").or(phoneValidationSchema),
    role: roleSchema,
  });

  const detailsForm = useAppForm({
    defaultValues: {
      username: user.username ?? "",
      email: user.email,
      phoneNumber: user.phoneNumber || "",
      role: user.role ?? "user",
    },
    validators: {
      onMount: detailsSchema,
      onChange: detailsSchema,
    },
    onSubmit: ({ value }) => {
      updateUser({
        id: userId,
        username: value.username || null,
        email: value.email,
        phoneNumber: value.phoneNumber
          ? normalizePhone(value.phoneNumber) || value.phoneNumber
          : null,
        role: value.role,
      });
    },
  });

  const { mutate: verifyEmail } = useMutation({
    ...adminVerifyEmailMutationOptions(),
    onSuccess: () => {
      queryClient.invalidateQueries(getUserQueryOptions(userId));
    },
  });

  const { mutate: verifyPhone } = useMutation({
    ...adminVerifyPhoneMutationOptions(),
    onSuccess: () => {
      queryClient.invalidateQueries(getUserQueryOptions(userId));
    },
  });

  const { mutate: impersonate } = useMutation({
    mutationFn: async () => {
      const { error } = await authClient.admin.impersonateUser({
        userId: user.id,
      });

      if (error) {
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries(trpc.viewer.get.queryOptions());
      queryClient.invalidateQueries(trpc.viewer.getImpersonator.queryOptions());
    },
  });

  return (
    <DefaultLayout
      classNames={{
        content: "py-12 w-full max-w-3xl mx-auto px-3 flex flex-col space-y-8",
      }}
    >
      <Suspense>
        <Link
          to="/admin/profiles"
          className={button({
            variant: "text",
            size: "sm",
            class: "self-start -ml-2",
          })}
        >
          <ArrowLeftIcon size={16} />
          Back to profiles
        </Link>

        <div className="flex flex-row items-start justify-between">
          <h1 className={title({ size: "sm" })}>{user.name}</h1>

          <DropdownMenu
            label="User actions"
            buttonIcon={<MoreVertical size={20} />}
          >
            <DropdownMenuItem
              id="verify-email"
              isDisabled={user.emailVerified}
              onAction={() => verifyEmail({ id: user.id })}
            >
              {user.emailVerified ? "Email Verified" : "Verify Email"}
            </DropdownMenuItem>
            <DropdownMenuItem
              id="verify-phone"
              isDisabled={!user.phoneNumber || user.phoneNumberVerified}
              onAction={() => verifyPhone({ id: user.id })}
            >
              {user.phoneNumberVerified ? "Phone Verified" : "Verify Phone"}
            </DropdownMenuItem>
            <DropdownMenuItem id="impersonate" onAction={() => impersonate()}>
              Impersonate
            </DropdownMenuItem>
            <DropdownMenuItem
              id="merge-user"
              onAction={() => setIsMergeUserOpen(true)}
            >
              Merge Another User
            </DropdownMenuItem>
            <DropdownMenuItem
              id="delete-user"
              className="text-red-600"
              onAction={() => setIsDeleteUserOpen(true)}
            >
              <Trash2Icon size={16} />
              Delete User
            </DropdownMenuItem>
          </DropdownMenu>
        </div>

        <div className={card({ class: "flex flex-col space-y-4 relative" })}>
          <div className="flex flex-row items-center justify-between">
            <h2 className="text-lg font-semibold">Account Details</h2>
            {!isEditingDetails && (
              <Button
                size="sm"
                variant="text"
                onPress={() => setEditingDetails(true)}
              >
                <EditIcon size={14} />
                Edit
              </Button>
            )}
          </div>

          {isEditingDetails ? (
            <form
              className="flex flex-col space-y-4"
              onSubmit={(e) => {
                e.preventDefault();
                detailsForm.handleSubmit();
              }}
            >
              {updateUserError && (
                <Alert
                  color="error"
                  title="Update failed"
                  description={updateUserError.message}
                />
              )}

              <div className="flex flex-col">
                <span className="text-sm text-gray-500">User ID</span>
                <span className="font-mono text-sm">{user.id}</span>
              </div>

              <div className="flex flex-col">
                <span className="text-sm text-gray-500">Name</span>
                <span>{user.name}</span>
                <span className="text-xs text-gray-400">
                  Edit from player profile
                </span>
              </div>

              <detailsForm.AppField
                name="username"
                children={(field) => (
                  <field.Text label="Username" field={field} />
                )}
              />

              <detailsForm.AppField
                name="role"
                children={(field) => (
                  <field.Select
                    label="Role"
                    field={field}
                    options={[
                      { display: "User", value: "user" },
                      { display: "Director", value: "td" },
                      { display: "Admin", value: "admin" },
                    ]}
                  />
                )}
              />

              <detailsForm.AppField
                name="email"
                children={(field) => (
                  <field.Text label="Email" type="email" field={field} />
                )}
              />

              <detailsForm.AppField
                name="phoneNumber"
                children={(field) => (
                  <field.Text label="Phone Number" type="tel" field={field} />
                )}
              />

              <detailsForm.AppForm>
                <detailsForm.Footer>
                  <Button onPress={() => setEditingDetails(false)}>
                    Cancel
                  </Button>
                  <detailsForm.SubmitButton>Save</detailsForm.SubmitButton>
                </detailsForm.Footer>
              </detailsForm.AppForm>
            </form>
          ) : (
            <div className="grid grid-cols-2 gap-4">
              <div className="flex flex-col">
                <span className="text-sm text-gray-500">User ID</span>
                <span className="font-mono text-sm">{user.id}</span>
              </div>

              <div className="flex flex-col">
                <span className="text-sm text-gray-500">Name</span>
                <span>{user.name}</span>
              </div>

              <div className="flex flex-col">
                <span className="text-sm text-gray-500">Username</span>
                <span>{user.username ?? "Not set"}</span>
              </div>

              <div className="flex flex-col">
                <span className="text-sm text-gray-500">Role</span>
                <span className="capitalize">{user.role ?? "user"}</span>
              </div>

              <div className="flex min-w-0 flex-col">
                <span className="text-sm text-gray-500">Email</span>
                <span className="flex min-w-0 items-center gap-1.5">
                  <span className="truncate">{user.email}</span>
                  <CopyButton value={user.email} size="xs" />
                  {user.emailVerified ? (
                    <CircleCheck
                      className="shrink-0 text-green-500"
                      size={14}
                    />
                  ) : (
                    <CircleX className="shrink-0 text-gray-400" size={14} />
                  )}
                </span>
              </div>

              <div className="flex flex-col">
                <span className="text-sm text-gray-500">Phone</span>
                <span className="flex items-center gap-1.5">
                  {user.phoneNumber || "Not set"}
                  {user.phoneNumber &&
                    (user.phoneNumberVerified ? (
                      <CircleCheck className="text-green-500" size={14} />
                    ) : (
                      <CircleX className="text-gray-400" size={14} />
                    ))}
                </span>
              </div>

              <div className="flex flex-col">
                <span className="text-sm text-gray-500">Created</span>
                <span>{new Date(user.createdAt).toLocaleDateString()}</span>
              </div>

              <div className="flex flex-col">
                <span className="text-sm text-gray-500">Updated</span>
                <span>{new Date(user.updatedAt).toLocaleDateString()}</span>
              </div>
            </div>
          )}
        </div>

        <div className={card({ class: "flex flex-col space-y-4" })}>
          <div className="flex flex-row items-center justify-between">
            <h2 className="text-lg font-semibold">
              Profiles ({user.userProfiles.length})
            </h2>
            <div className="flex flex-row items-center gap-x-2">
              {!isMergeMode && (
                <Button
                  size="sm"
                  variant="text"
                  onPress={() => setIsLinkDependentOpen(true)}
                >
                  <UserPlusIcon size={14} />
                  Link Dependent
                </Button>
              )}
              {user.userProfiles.length >= 2 &&
                (isMergeMode ? (
                  <Button
                    size="sm"
                    variant="text"
                    onPress={() => {
                      setMergeKeepId(null);
                      setMergeDeleteId(null);
                    }}
                  >
                    <XIcon size={14} />
                    Cancel Merge
                  </Button>
                ) : (
                  <Button
                    size="sm"
                    variant="text"
                    onPress={() => setMergeKeepId(-1)}
                  >
                    <Merge size={14} />
                    Merge
                  </Button>
                ))}
            </div>
          </div>

          {isMergeMode && !mergeDeleteId && (
            <p className="text-sm text-gray-500">
              {mergeKeepId === -1
                ? "Select the profile to keep."
                : "Now select the profile to delete."}
            </p>
          )}

          {user.userProfiles.length > 0 ? (
            <ul className="divide-y divide-gray-200">
              {[...user.userProfiles]
                .sort((a, b) => {
                  if (a.role === "self" && b.role !== "self") return -1;
                  if (a.role !== "self" && b.role === "self") return 1;
                  return 0;
                })
                .map(({ role, playerProfile: profile }) => {
                  const isKeep = mergeKeepId === profile.id;
                  const isSelectable = isMergeMode && !mergeDeleteId && !isKeep;
                  const handleMergeClick = () => {
                    if (mergeKeepId === -1) {
                      setMergeKeepId(profile.id);
                    } else if (
                      mergeKeepId !== null &&
                      mergeKeepId !== profile.id
                    ) {
                      setMergeDeleteId(profile.id);
                    }
                  };

                  return (
                    <li
                      key={profile.id}
                      role={isSelectable ? "button" : undefined}
                      tabIndex={isSelectable ? 0 : undefined}
                      onClick={isSelectable ? handleMergeClick : undefined}
                      onKeyDown={
                        isSelectable
                          ? (e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                handleMergeClick();
                              }
                            }
                          : undefined
                      }
                      className={`flex flex-col gap-2 py-3 ${
                        isSelectable
                          ? "-mx-4 cursor-pointer rounded px-4 hover:bg-gray-50"
                          : ""
                      } ${isKeep ? "-mx-4 rounded bg-green-50 px-4" : ""}`}
                    >
                      <div className="flex flex-row items-center justify-between gap-x-3">
                        <span className="flex flex-1 flex-row items-center gap-x-2">
                          {profile.imageSource ? (
                            <img
                              className="h-8 w-8 overflow-hidden rounded-full border border-gray-300"
                              src={profile.imageSource}
                              alt={profile.preferredName || profile.firstName}
                            />
                          ) : (
                            <UserCircleIcon className="h-8 w-8 text-gray-400" />
                          )}

                          <div className="flex flex-col">
                            <span className="flex items-center gap-2">
                              <ProfileName
                                id={profile.id}
                                firstName={profile.firstName}
                                preferredName={profile.preferredName}
                                lastName={profile.lastName}
                                link={!isMergeMode}
                              />
                              {role === "guardian" && (
                                <span className={tagStyles({ color: "blue" })}>
                                  Dependent
                                </span>
                              )}
                              {isKeep && (
                                <span className={tagStyles({ color: "green" })}>
                                  Keeping
                                </span>
                              )}
                            </span>
                            {role !== "guardian" && (
                              <span className="text-sm text-gray-500">
                                {profile.gender} |{" "}
                                {profile.level?.name ?? "Unrated"}
                              </span>
                            )}
                          </div>
                        </span>

                        {!isMergeMode && (
                          <div className="flex flex-row items-center gap-x-3">
                            {role !== "guardian" &&
                              (profile.activeMembership ? (
                                <span className={tagStyles({ color: "green" })}>
                                  <CheckIcon size={12} /> Member
                                </span>
                              ) : (
                                <>
                                  <span
                                    className={tagStyles({ color: "gray" })}
                                  >
                                    No Membership
                                  </span>
                                  <Button
                                    variant="text"
                                    size="xs"
                                    onPress={() =>
                                      setCreateMembershipProfileId(profile.id)
                                    }
                                  >
                                    <PlusIcon size={14} />
                                    Create Membership
                                  </Button>
                                </>
                              ))}

                            {role === "guardian" && (
                              <Button
                                variant="text"
                                size="xs"
                                className="text-red-600 hover:text-red-700"
                                onPress={() => setUnlinkProfileId(profile.id)}
                              >
                                <UserMinusIcon size={14} />
                                Unlink
                              </Button>
                            )}

                            <Button
                              variant="text"
                              size="xs"
                              onPress={() => setEditingProfileId(profile.id)}
                            >
                              <EditIcon size={14} />
                              Edit
                            </Button>
                          </div>
                        )}
                      </div>

                      {!isMergeMode && (
                        <ProfileRegistrations
                          profileId={profile.id}
                          showActions
                        />
                      )}

                      {!isMergeMode && profile.activeMembership && (
                        <>
                          <hr className="mx-2 mt-2 border-gray-200" />
                          <TshirtSizeEditor
                            membership={profile.activeMembership}
                            onUpdated={() =>
                              queryClient.invalidateQueries(
                                getUserQueryOptions(userId),
                              )
                            }
                          />
                        </>
                      )}
                    </li>
                  );
                })}
            </ul>
          ) : (
            <p className="text-gray-500">
              No profiles associated with this user.
            </p>
          )}
        </div>

        {editingProfileId && (
          <EditProfileModal
            profileId={editingProfileId}
            profileName={
              user.userProfiles.find(
                (up) => up.playerProfile.id === editingProfileId,
              )?.playerProfile.firstName ?? ""
            }
            isOpen={true}
            onOpenChange={(open) => {
              if (!open) {
                setEditingProfileId(null);
                queryClient.invalidateQueries(getUserQueryOptions(userId));
              }
            }}
          />
        )}

        {createMembershipProfileId && (
          <CreateMembershipModal
            profileId={createMembershipProfileId}
            profileName={
              user.userProfiles.find(
                (up) => up.playerProfile.id === createMembershipProfileId,
              )?.playerProfile.firstName ?? ""
            }
            isOpen={true}
            onOpenChange={(open) => {
              if (!open) {
                setCreateMembershipProfileId(null);
                queryClient.invalidateQueries(getUserQueryOptions(userId));
              }
            }}
          />
        )}

        <DialogTrigger
          isOpen={!!unlinkProfileId}
          onOpenChange={(open) => {
            if (!open) {
              setUnlinkProfileId(null);
              resetUnlink();
            }
          }}
        >
          <Modal isDismissable>
            <div className="flex flex-col space-y-4 p-6">
              <ModalHeading size="sm">Unlink Dependent</ModalHeading>

              <p>
                Are you sure you want to unlink{" "}
                <span className="font-semibold">
                  {(() => {
                    const p = user.userProfiles.find(
                      (up) => up.playerProfile.id === unlinkProfileId,
                    )?.playerProfile;
                    return p?.preferredName || p?.firstName;
                  })()}
                </span>{" "}
                from this account?
              </p>

              <p className="text-sm text-gray-600">
                This will remove the guardian relationship. The profile and any
                associated account will not be deleted.
              </p>

              {unlinkError && (
                <Alert
                  color="error"
                  title="Error"
                  description={unlinkError.message}
                />
              )}

              <div className="flex justify-end gap-3">
                <Button
                  isDisabled={isUnlinking}
                  onPress={() => setUnlinkProfileId(null)}
                >
                  Cancel
                </Button>
                <Button
                  color="danger"
                  isDisabled={isUnlinking}
                  onPress={() =>
                    unlinkProfileId &&
                    unlinkDependent({ userId, profileId: unlinkProfileId })
                  }
                >
                  {isUnlinking ? "Unlinking..." : "Unlink"}
                </Button>
              </div>
            </div>
          </Modal>
        </DialogTrigger>

        <DialogTrigger
          isOpen={!!mergeDeleteId}
          onOpenChange={(open) => {
            if (!open) setMergeDeleteId(null);
          }}
        >
          <Modal isDismissable size="lg">
            <div className="flex flex-col gap-4 p-4">
              <Heading className={title({ size: "sm" })} slot="title">
                Merge Profiles
              </Heading>

              {(() => {
                const keepUp = user.userProfiles.find(
                  (up) => up.playerProfile.id === mergeKeepId,
                );
                const deleteUp = user.userProfiles.find(
                  (up) => up.playerProfile.id === mergeDeleteId,
                );
                if (!keepUp || !deleteUp) return null;

                const toMergeProfile = (
                  p: (typeof user.userProfiles)[number]["playerProfile"],
                ): MergeProfile => ({
                  id: p.id,
                  userId: p.userId,
                  firstName: p.firstName,
                  lastName: p.lastName,
                  preferredName: p.preferredName,
                  birthdate: p.birthdate,
                  gender: p.gender,
                  levelId: p.levelId,
                  levelName: p.level?.name ?? null,
                  levelAbbreviated: p.level?.abbreviated ?? null,
                  imageSource: p.imageSource,
                  ratedPoints: p.ratedPoints,
                  bio: p.bio,
                  heightFeet: p.heightFeet,
                  heightInches: p.heightInches,
                  dominantArm: p.dominantArm,
                  preferredRole: p.preferredRole,
                  preferredSide: p.preferredSide,
                  club: p.club,
                  highSchoolGraduationYear: p.highSchoolGraduationYear,
                  collegeTeam: p.collegeTeam,
                  collegeTeamYearsParticipated: p.collegeTeamYearsParticipated,
                  avpId: p.avpId,
                  externalRef: p.externalRef,
                  juniorsPoints: p.juniorsPoints,
                  rank: p.rank,
                  collegeCommitmentLevel: p.collegeCommitmentLevel,
                  createdAt: p.createdAt,
                  updatedAt: p.updatedAt,
                  deletedAt: p.deletedAt,
                  activeMembership: null,
                  revokedMembership: null,
                  lastActiveAt: null,
                  user: null,
                  guardian: null,
                  dependents: [],
                });

                return (
                  <MergeProfilesForm
                    keepProfile={toMergeProfile(keepUp.playerProfile)}
                    deleteProfile={toMergeProfile(deleteUp.playerProfile)}
                    onSuccess={() => {
                      setMergeKeepId(null);
                      setMergeDeleteId(null);
                      queryClient.invalidateQueries(
                        getUserQueryOptions(userId),
                      );
                    }}
                    onCancel={() => setMergeDeleteId(null)}
                  />
                );
              })()}
            </div>
          </Modal>
        </DialogTrigger>

        <DialogTrigger
          isOpen={isMergeUserOpen}
          onOpenChange={(open) => {
            if (!open) setIsMergeUserOpen(false);
          }}
        >
          <Modal isDismissable>
            <div className="flex max-w-md flex-col space-y-4 p-6">
              <Heading className={title({ size: "sm" })} slot="title">
                Merge Another User
              </Heading>
              <MergeUserForm
                keepUserId={userId}
                keepUserName={user.name}
                onSuccess={() => {
                  setIsMergeUserOpen(false);
                  queryClient.invalidateQueries(getUserQueryOptions(userId));
                }}
                onCancel={() => setIsMergeUserOpen(false)}
              />
            </div>
          </Modal>
        </DialogTrigger>

        <DeleteUserModal
          userId={userId}
          userName={user.name}
          isOpen={isDeleteUserOpen}
          onOpenChange={setIsDeleteUserOpen}
          onDeleted={() => navigate({ to: "/admin/profiles" })}
        />

        <LinkDependentModal
          userId={userId}
          userName={user.name}
          isOpen={isLinkDependentOpen}
          onOpenChange={setIsLinkDependentOpen}
          onLinked={() => {
            setIsLinkDependentOpen(false);
            queryClient.invalidateQueries(getUserQueryOptions(userId));
          }}
        />
      </Suspense>
    </DefaultLayout>
  );
}

function MergeUserForm({
  keepUserId,
  keepUserName,
  onSuccess,
  onCancel,
}: {
  keepUserId: string;
  keepUserName: string;
  onSuccess: () => void;
  onCancel: () => void;
}) {
  const trpc = useTRPC();
  const [deleteUserId, setDeleteUserId] = useState("");
  const [isConfirming, setIsConfirming] = useState(false);

  const {
    data: deleteUserSummary,
    isLoading: isSummaryLoading,
    error: summaryError,
  } = useQuery({
    ...trpc.users.getSummary.queryOptions({ userId: deleteUserId }),
    enabled: deleteUserId.length > 0 && deleteUserId !== keepUserId,
  });

  const {
    mutate,
    isPending,
    error: mergeError,
  } = useMutation({
    ...trpc.users.mergeUsers.mutationOptions(),
    onSuccess,
  });

  const isSameUser = deleteUserId === keepUserId;

  return (
    <div className="flex flex-col space-y-4">
      <p className="text-sm text-gray-600">
        Merge another user&apos;s account into{" "}
        <span className="font-semibold">{keepUserName}</span>. All profiles,
        invoices, and other data from the other account will be transferred, and
        the other account will be deleted.
      </p>

      <TextField
        label="User ID to merge"
        value={deleteUserId}
        onChange={setDeleteUserId}
        placeholder="Paste user ID here..."
      />

      {isSameUser && (
        <Alert
          color="warning"
          title="Same user"
          description="You cannot merge a user into itself."
        />
      )}

      {summaryError && (
        <Alert
          color="error"
          title="User not found"
          description="No user exists with that ID."
        />
      )}

      {deleteUserSummary && (
        <div className="space-y-1 rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm">
          <div>
            <span className="text-gray-500">Name:</span>{" "}
            {deleteUserSummary.name}
          </div>
          <div>
            <span className="text-gray-500">Email:</span>{" "}
            {deleteUserSummary.email}
          </div>
          <div>
            <span className="text-gray-500">Role:</span>{" "}
            <span className="capitalize">
              {deleteUserSummary.role ?? "user"}
            </span>
          </div>
          <div>
            <span className="text-gray-500">Profiles:</span>{" "}
            {deleteUserSummary.profileCount}
          </div>
        </div>
      )}

      {mergeError && (
        <Alert
          color="error"
          title="Merge failed"
          description={mergeError.message}
        />
      )}

      {isConfirming && (
        <Alert
          color="warning"
          title="This action is permanent"
          description={`The account for ${deleteUserSummary?.name ?? "this user"} will be permanently deleted. All their data will be transferred to ${keepUserName}.`}
        />
      )}

      <div className="flex justify-end gap-3">
        <Button
          onPress={isConfirming ? () => setIsConfirming(false) : onCancel}
        >
          Cancel
        </Button>
        {isConfirming ? (
          <Button
            color="danger"
            isDisabled={isPending}
            onPress={() => mutate({ keepUserId, deleteUserId })}
          >
            {isPending ? "Merging..." : "Yes, Delete Account"}
          </Button>
        ) : (
          <Button
            color="primary"
            isDisabled={
              !deleteUserSummary || isPending || isSummaryLoading || isSameUser
            }
            onPress={() => setIsConfirming(true)}
          >
            Confirm Merge
          </Button>
        )}
      </div>
    </div>
  );
}

function CreateMembershipModal({
  profileId,
  profileName,
  isOpen,
  onOpenChange,
}: {
  profileId: number;
  profileName: string;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const trpc = useTRPC();
  const [tshirtSize, setTshirtSize] = useState<TshirtSize | null>(null);

  const { mutate, isPending, error } = useMutation({
    ...trpc.tournaments.createMembership.mutationOptions(),
    onSuccess: () => {
      onOpenChange(false);
    },
  });

  return (
    <Modal isDismissable isOpen={isOpen} onOpenChange={onOpenChange}>
      <div className="flex flex-col space-y-4 p-6">
        <ModalHeading size="sm">Create Membership</ModalHeading>

        <p className="text-sm text-gray-600">
          Create a membership for{" "}
          <span className="font-semibold">{profileName}</span> without requiring
          payment.
        </p>

        <Select
          label="T-Shirt Size"
          options={TSHIRT_SIZE_OPTIONS}
          placeholder="Select size (optional)"
          selectedKey={tshirtSize}
          onSelectionChange={(key) => setTshirtSize(key as TshirtSize)}
        />

        {error && (
          <Alert
            color="error"
            title="Failed to create membership"
            description={error.message}
          />
        )}

        <div className="flex justify-end gap-3">
          <Button onPress={() => onOpenChange(false)}>Cancel</Button>
          <Button
            color="primary"
            isDisabled={isPending}
            onPress={() => mutate({ profileId, tshirtSize })}
          >
            {isPending ? "Creating..." : "Create"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

const linkDependentSchema = z.object({
  profileId: z.number({ error: "Select a profile" }),
});

function LinkDependentModal({
  userId,
  userName,
  isOpen,
  onOpenChange,
  onLinked,
}: {
  userId: string;
  userName: string;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onLinked: () => void;
}) {
  const trpc = useTRPC();

  const { mutate, isPending, error, reset } = useMutation({
    ...trpc.users.adminLinkDependent.mutationOptions(),
    onSuccess: onLinked,
  });

  const needsOverride = error?.data?.code === "CONFLICT";

  const form = useAppForm({
    defaultValues: { profileId: null as number | null },
    validators: { onChange: linkDependentSchema },
    onSubmit: ({ value }) => {
      if (value.profileId != null) {
        mutate({ userId, profileId: value.profileId });
      }
    },
  });

  return (
    <DialogTrigger
      isOpen={isOpen}
      onOpenChange={(open) => {
        if (!open) {
          reset();
          form.reset();
        }
        onOpenChange(open);
      }}
    >
      <Modal isDismissable>
        <form
          className="flex max-w-md flex-col space-y-4 p-6"
          onSubmit={(e) => {
            e.preventDefault();
            form.handleSubmit();
          }}
        >
          <ModalHeading size="sm">Link Dependent Profile</ModalHeading>

          <p className="text-sm text-gray-600">
            Search for an existing profile to link as a dependent of{" "}
            <span className="font-semibold">{userName}</span>. This account will
            become a guardian able to manage the profile.
          </p>

          <form.AppField
            name="profileId"
            children={(field) => (
              <field.ProfilePicker
                label="Profile"
                field={field}
                selectedProfileIds={
                  field.state.value != null ? [field.state.value] : []
                }
              />
            )}
          />

          {error && (
            <Alert
              color={needsOverride ? "warning" : "error"}
              title={needsOverride ? "Already managed" : "Failed to link"}
              description={error.message}
            />
          )}

          <div className="flex justify-end gap-3">
            <Button onPress={() => onOpenChange(false)} isDisabled={isPending}>
              Cancel
            </Button>
            {needsOverride ? (
              <Button
                color="danger"
                isDisabled={isPending}
                onPress={() => {
                  const profileId = form.state.values.profileId;
                  if (profileId != null) {
                    mutate({ userId, profileId, force: true });
                  }
                }}
              >
                {isPending ? "Linking..." : "Link Anyway"}
              </Button>
            ) : (
              <form.AppForm>
                <form.SubmitButton>
                  {isPending ? "Linking..." : "Link"}
                </form.SubmitButton>
              </form.AppForm>
            )}
          </div>
        </form>
      </Modal>
    </DialogTrigger>
  );
}

function DeleteUserModal({
  userId,
  userName,
  isOpen,
  onOpenChange,
  onDeleted,
}: {
  userId: string;
  userName: string;
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  onDeleted: () => void;
}) {
  const trpc = useTRPC();

  const { data: preview, isLoading: isLoadingPreview } = useQuery({
    ...trpc.users.getDeletionPreview.queryOptions({ userId }),
    enabled: isOpen,
  });

  return (
    <DialogTrigger isOpen={isOpen} onOpenChange={onOpenChange}>
      <Modal isDismissable size="lg">
        <div className="flex max-w-2xl flex-col space-y-4 p-6">
          <ModalHeading size="sm">Delete {userName}</ModalHeading>

          {isLoadingPreview && (
            <p className="text-sm text-gray-600">Checking this account…</p>
          )}

          {/* Mounts only once the preview lands, so the form's defaults can be
              built from the real dependent list and closing the modal resets
              every field for free. */}
          {preview && (
            <DeleteUserConfirmForm
              userId={userId}
              preview={preview}
              onCancel={() => onOpenChange(false)}
              onDeleted={() => {
                onOpenChange(false);
                onDeleted();
              }}
            />
          )}
        </div>
      </Modal>
    </DialogTrigger>
  );
}

/** Case and surrounding whitespace are meaningless; anything else is not. */
function normalizeConfirmName(name: string) {
  return name.trim().toLowerCase();
}

function DeleteUserConfirmForm({
  userId,
  preview,
  onCancel,
  onDeleted,
}: {
  userId: string;
  preview: inferRouterOutputs<typeof usersRouter>["getDeletionPreview"];
  onCancel: () => void;
  onDeleted: () => void;
}) {
  const trpc = useTRPC();

  const { mutate, isPending, error } = useMutation(
    trpc.users.forceDeleteUser.mutationOptions({ onSuccess: onDeleted }),
  );

  const dependents = preview.dependents;
  const blockers = preview.blockingRegistrations;
  const isBlocked = blockers.length > 0;

  const form = useAppForm({
    defaultValues: {
      confirmName: "",
      // One decision per dependent, positional so the fields can be named
      // `dependents[i]` (FR-006c). Empty action means "not decided yet".
      dependents: dependents.map((dependent) => ({
        profileId: dependent.profileId,
        action: "" as "" | "reassign" | "anonymize",
        toProfileId: null as number | null,
      })),
    },
    onSubmit: ({ value }) => {
      mutate({
        userId,
        confirmName: value.confirmName,
        dependents: value.dependents.map((decision) => ({
          profileId: decision.profileId,
          action: decision.action as "reassign" | "anonymize",
          toProfileId: decision.toProfileId ?? undefined,
        })),
      });
    },
  });

  return (
    <form
      className="flex flex-col space-y-4"
      onSubmit={(e) => {
        e.preventDefault();
        form.handleSubmit();
      }}
    >
      <Alert
        color="error"
        title="This is permanent and cannot be undone"
        description={
          <>
            Erases{" "}
            {preview.email ? <strong>{preview.email}</strong> : "this person"}
            's login and personal information. Their tournament history stays,
            shown as <strong>Deleted user</strong>. To fix a duplicate account,
            use <strong>Merge Another</strong> User instead.
          </>
        }
      />

      {isBlocked && (
        <Alert
          color="warning"
          title="Resolve these registrations first"
          description={`This account still has unresolved participation. Deletion never withdraws a team or issues a refund — that has to be done deliberately. ${blockers
            .map(
              (blocker) =>
                `${blocker.tournamentDisplay} (${
                  blocker.reason === "roster"
                    ? "on the roster"
                    : "paid for a team"
                })`,
            )
            .join("; ")}`}
        />
      )}

      {preview.vacatingRoles.venues.length > 0 ||
      preview.vacatingRoles.tournaments.length > 0 ? (
        <Alert
          color="warning"
          title="These will be left without a director"
          description={[
            ...preview.vacatingRoles.venues.map((venue) => venue.name),
            ...preview.vacatingRoles.tournaments.map(
              (tournament) => tournament.display,
            ),
          ].join("; ")}
        />
      ) : null}

      {dependents.length > 0 && (
        <div className="space-y-3">
          <p className="text-sm font-semibold text-gray-800">
            This account is the only guardian of{" "}
            {dependents.length === 1
              ? "one person"
              : `${dependents.length} people`}
            . Decide what happens to each.
          </p>

          {dependents.map((dependent, index) => (
            <div
              key={dependent.profileId}
              className="rounded-md border border-gray-200 p-3"
            >
              <p className="text-sm font-semibold text-gray-800">
                {dependent.name}
              </p>

              {dependent.blockingRegistrations.length > 0 && (
                <p className="mt-1 text-sm text-amber-700">
                  Cannot be deleted yet — registered for{" "}
                  {dependent.blockingRegistrations
                    .map((blocker) => blocker.tournamentDisplay)
                    .join(", ")}
                  . Reassign them, or resolve the registration first.
                </p>
              )}

              <div className="mt-2 space-y-2">
                <form.AppField name={`dependents[${index}].action`}>
                  {(field) => (
                    <field.RadioGroup
                      field={field}
                      mode="string"
                      aria-label={`What happens to ${dependent.name}`}
                      isDisabled={isBlocked || isPending}
                    >
                      <Radio value="reassign">Give to another account</Radio>
                      <Radio value="anonymize">Delete them too</Radio>
                    </field.RadioGroup>
                  )}
                </form.AppField>

                <form.Subscribe
                  selector={(state) => state.values.dependents[index]?.action}
                >
                  {(action) =>
                    action === "reassign" ? (
                      <form.AppField name={`dependents[${index}].toProfileId`}>
                        {(field) => (
                          <field.ProfilePicker
                            label="Who takes over"
                            field={field}
                            isDisabled={isBlocked || isPending}
                            selectedProfileIds={
                              field.state.value != null
                                ? [field.state.value]
                                : []
                            }
                          />
                        )}
                      </form.AppField>
                    ) : null
                  }
                </form.Subscribe>
              </div>
            </div>
          ))}
        </div>
      )}

      <form.AppField name="confirmName">
        {(field) => (
          <field.Text
            field={field}
            label={
              <>
                Type <strong>{preview.confirmName}</strong> to confirm
              </>
            }
            isDisabled={isBlocked || isPending}
            autoComplete="off"
          />
        )}
      </form.AppField>

      {error && (
        <Alert color="error" title="Not deleted" description={error.message} />
      )}

      <div className="flex justify-end gap-3">
        <Button onPress={onCancel} isDisabled={isPending}>
          Cancel
        </Button>
        <form.Subscribe selector={(state) => state.values}>
          {(values) => (
            <form.AppForm>
              <form.SubmitButton
                color="danger"
                isDisabled={
                  isBlocked ||
                  isPending ||
                  normalizeConfirmName(values.confirmName) !==
                    normalizeConfirmName(preview.confirmName) ||
                  !values.dependents.every(
                    (decision) =>
                      decision.action === "anonymize" ||
                      (decision.action === "reassign" &&
                        decision.toProfileId != null),
                  )
                }
              >
                {isPending ? "Deleting..." : "Permanently delete"}
              </form.SubmitButton>
            </form.AppForm>
          )}
        </form.Subscribe>
      </div>
    </form>
  );
}
